<template>
  <div class="appointment-form">
    <div class="appointment-mode-banner" :class="isUpdateMode ? 'mode-update' : 'mode-new'">
      {{ isUpdateMode ? 'Update Appointment' : 'New Appointment' }}
    </div>
    <v-form ref="appointmentform" @submit.prevent="processAppointment">
      <v-row>
        <v-col sm="6">
          <v-text-field
            v-model="appointmentForm.customer.firstname"
            label="First Name"
            variant="outlined"
            density="compact"
            single-line
            hide-details
            required
            :rules="nameRules"
          ></v-text-field>
        </v-col>
        <v-col sm="6">
          <v-text-field
            v-model="appointmentForm.customer.lastname"
            label="Last Name"
            variant="outlined"
            density="compact"
            single-line
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col col="12" sm="12">
          <v-text-field
            v-model="appointmentForm.customer.phone"
            label="Phone"
            density="compact"
            variant="outlined"
            :error-messages="errors.phone"
            single-line
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col col="12" sm="12" class="">
          <v-btn size="x-small" variant="tonal" @click="generateEmail" v-show="false"
            >Generate</v-btn
          >
          <v-text-field
            v-model="appointmentForm.customer.email"
            label="Email"
            density="compact"
            variant="outlined"
            single-line
            :rules="conditionalEmailRules"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col col="12" sm="12" class="pb-4">
          <v-checkbox
            v-model="appointmentForm.customer.marketing"
            label="Email marketing"
            density="compact"
            hide-details
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12">
          <VueCtkDateTimePicker
            v-model="appointmentForm.datetime"
            minute-interval="15"
            format-output="YYYY-MM-DDTHH:mm:ss.sssZ"
            :disabled-hours="[
              '00',
              '01',
              '02',
              '03',
              '04',
              '05',
              '06',
              '07',
              '22',
              '23',
              '24',
            ]"
            @formatted-value="update_stylist_list"
          ></VueCtkDateTimePicker>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12">
          <v-select
            v-model="appointmentForm.service_id"
            class="black-border"
            :items="servicesList"
            item-title="description"
            item-value="id"
            label="Select Services"
            density="compact"
            hide-details
            multiple
            bg-color="white"
          >
          </v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12">
          <v-autocomplete
            v-model="appointmentForm.stylist"
            class="black-border"
            :items="resourcesLists"
            item-title="name"
            item-value="id"
            label="Select Stylist"
            density="compact"
            hide-details
            bg-color="white"
          >
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12">
          <v-textarea
            v-model="appointmentForm.note"
            label="Note"
            class="black-border note-block"
            density="compact"
            bg-color="white"
            base-color="white"
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row style="margin: 0px !important" id="form-buttons">
        <v-col class="px-1">
          <v-btn
            color="blue"
            variant="tonal"
            type="submit"
            :disabled="submitted"
            block
          >
            Save
          </v-btn>
        </v-col>
        <v-col class="px-1">
          <v-btn
            class="pl-2"
            color="red"
            variant="tonal"
            block
            @click="closeForm"
          >
            Close
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar
      v-model="showErrorSnackbar"
      :timeout="5000"
      color="error"
      location="top"
      multi-line
    >
      <div class="text-subtitle-1 pb-2">{{ errorMessage }}</div>
      <ul v-if="errorDetails.length > 0" class="pl-4">
        <li v-for="(error, index) in errorDetails" :key="index">{{ error }}</li>
      </ul>
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="showErrorSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { useDateStore } from "../../../../stores/datestore";
import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
import "vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css";
import { servicesList, resourcesList } from "./../../../../libs/options";
import { useEventStore } from "../../../../stores/EventStore";
import { useAppointmentStore } from "../../../../stores/appointmentsStore";
import { useEditStore } from "../../../../stores/EditFormStore";
import axios from "axios";
import { computed } from "vue";
import { sendAppointmentNote } from "./../../../../libs/notesHelper";

export default {
  name: "EventForm",
  components: { VueCtkDateTimePicker },
  props: {
    event: {
      type: Object,
      default: () => {},
    },
  },
  emits: ["form-submitted", "closeForm"],
  setup(props) {
    const eventStore = useEventStore();
    const dateStore = useDateStore();
    const apptStore = useAppointmentStore();
    const editStore = useEditStore();
    let eventObj =
      props.event.data !== undefined && props.event.data.id
        ? eventStore.getEvent(props.event.data.id)
        : {};

    // If in clone mode, remove the id so it creates a new appointment
    if (editStore.isCloneMode && eventObj.id) {
      eventObj = { ...eventObj, id: undefined };
    }

    // Handle temp events from client modal clone
    if (editStore.editFormId && typeof editStore.editFormId === 'string' && editStore.editFormId.startsWith('temp-')) {
      eventObj = eventStore.events[editStore.editFormId] || {};
      // Clean up temp event
      delete eventStore.events[editStore.editFormId];
    }

    // Check if there's a pre-filled customer from client modal
    if (eventStore.prefilledCustomer && !eventObj.customer) {
      eventObj = {
        ...eventObj,
        customer: eventStore.prefilledCustomer
      };
      // Clear the prefilled data
      eventStore.prefilledCustomer = null;
    }

    const stylistResources = computed(() => {
      return apptStore.getStylists.map((el) => ({ id: el.id, name: el.name }));
    });
    return {
      dateStore,
      resourcesList,
      servicesList,
      eventObj,
      eventStore,
      apptStore,
      editStore,
      stylistResources,
    };
  },
  data() {
    return {
      resourcesLists: [],
      appointmentForm: Object.assign(
        {
          customer: {
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            marketing: false,
          },
          datetime: new Date(new Date().setUTCHours(8, 0, 0)),
          services: [],
          service_id: [],
          stylist: null,
          note: "",
        },
        this.eventObj ? this.eventObj : {},
        // Ensure customer object is properly merged and marketing is converted to boolean
        this.eventObj && this.eventObj.customer ? {
          customer: Object.assign(
            {
              firstname: "",
              lastname: "",
              email: "",
              phone: "",
              marketing: false,
            },
            this.eventObj.customer,
            {
              marketing: !!this.eventObj.customer.marketing
            }
          )
        } : {}
      ),
      errors: {},
      submitted: false,
      showErrorSnackbar: false,
      errorMessage: '',
      errorDetails: [],
      emailRules: [
        (value) => !!value || "Email is required.",
        (value) => value.indexOf("@") !== 0 || "Email should have a Username.",
        (value) => value.includes("@") || "Email should include an @ symbol.",
        (value) =>
          value.lastIndexOf(".") - value.indexOf("@") > 1 ||
          "Email should contain a valid domain.",
        (value) =>
          value.indexOf(".") <= value.length - 3 ||
          "Email should contain a valid domain extension",
      ],
      nameRules: [(value) => !!value || "is required"],
    };
  },
  computed: {
    isUpdateMode() {
      return !!this.appointmentForm.id;
    },
    selectOptionsList() {
      return this.resourcesLists.map((item) => ({
        id: item.id,
        name: item.name,
      }));
    },
    is_stylist_disabled() {
      return false;
    },
    conditionalEmailRules() {
      // If opt-in checkbox is checked, email is required
      if (this.appointmentForm.customer.marketing) {
        return this.emailRules;
      }
      // If not checked, only validate format if email is provided
      return [
        (value) => {
          if (!value) return true; // Allow empty
          return value.indexOf("@") !== 0 || "Email should have a Username.";
        },
        (value) => {
          if (!value) return true;
          return value.includes("@") || "Email should include an @ symbol.";
        },
        (value) => {
          if (!value) return true;
          return value.lastIndexOf(".") - value.indexOf("@") > 1 || "Email should contain a valid domain.";
        },
        (value) => {
          if (!value) return true;
          return value.indexOf(".") <= value.length - 3 || "Email should contain a valid domain extension";
        },
      ];
    },
  },
  watch: {
    "editStore.editFormId"() {
      this.loadAppointmentData();
    },
    "editStore.isCloneMode"() {
      this.loadAppointmentData();
    },
    "appointmentForm.service_id"(new_value, old_value) {
      this.appointmentForm.services = [];
      new_value.forEach((service_id) => {
        this.appointmentForm.services.push(
          this.servicesList.find((el) => el.id === service_id)
        );
      });
      // let addedId = new_value.filter((x) => !old_value.includes(x)).shift();
      // this.appointmentForm.services.push(
      //   this.servicesList.find((el) => el.id === addedId)
      // );
    },
  },
  created() {
    this.appointmentForm.datetime = this.appointmentForm.datetime.toISOString();
  },
  methods: {
    loadAppointmentData() {
      let eventData = {};
      if (this.editStore.editFormId) {
        eventData = this.eventStore.getEvent(this.editStore.editFormId) || {};

        // If in clone mode, remove the id so it creates a new appointment
        if (this.editStore.isCloneMode && eventData.id) {
          eventData = { ...eventData, id: undefined };
        }
      }

      // Reset form to default values then merge with event data
      this.appointmentForm = Object.assign(
        {
          customer: {
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            marketing: false,
          },
          datetime: new Date(new Date().setUTCHours(8, 0, 0)),
          services: [],
          service_id: [],
          stylist: null,
          note: "",
        },
        eventData,
        // Ensure customer object is properly merged and marketing is converted to boolean
        eventData && eventData.customer ? {
          customer: Object.assign(
            {
              firstname: "",
              lastname: "",
              email: "",
              phone: "",
              marketing: false,
            },
            eventData.customer,
            {
              marketing: !!eventData.customer.marketing
            }
          )
        } : {}
      );

      // Convert datetime to ISO string for the date picker
      if (this.appointmentForm.datetime) {
        this.appointmentForm.datetime = this.appointmentForm.datetime.toISOString();
      }
    },
    processAppointment() {
      this.submitted = true;
      this.errors = {};
      if (
        typeof this.appointmentForm.datetime === "string" &&
        this.appointmentForm.datetime.indexOf("T") === -1
      ) {
        this.appointmentForm.datetime += " UTC";
      }
      this.appointmentForm.datetime = new Date(
        Date.parse(this.appointmentForm.datetime)
      );

      if (this.appointmentForm.id) {
        this.eventStore.updateEvent(this.appointmentForm);
        // Send note for appointment update
        sendAppointmentNote(
          this.appointmentForm.customer,
          'Appointment updated',
          this.appointmentForm.note || '',
          {
            id: this.appointmentForm.id,
            service_id: this.appointmentForm.service_id
          }
        ).catch(err => console.error('Failed to send update note:', err));
        this.$emit("closeForm");
      } else {
        // generating random Id for now; remove later; ToDo
        axios
          .post("/api/customer", this.appointmentForm.customer)
          .then((response) => {
            this.appointmentForm.customer.id = response.data.customer_id;
            axios
              .post("/api/bookings", {
                customer_id: response.data.customer_id,
                service_id: this.appointmentForm.service_id,
                stylist: this.appointmentForm.stylist,
                datetime: this.appointmentForm.datetime
                  .toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "UTC",
                    hour12: false, // Set to false to use 24-hour clock
                  })
                  .replace(",", ""),
                note: this.appointmentForm.note
              })
              .then((response) => {
                this.appointmentForm.id = response.data.appointment_id;
                this.eventStore.addEvent(this.appointmentForm);
                // Send note for new appointment
                sendAppointmentNote(
                  this.appointmentForm.customer,
                  'Appointment created',
                  this.appointmentForm.note || '',
                  {
                    id: this.appointmentForm.id,
                    service_id: this.appointmentForm.service_id
                  }
                ).catch(err => console.error('Failed to send creation note:', err));

                // Only close the form after booking is successfully created
                this.$emit("closeForm");
              })
              .catch((error) => {
                // Handle booking creation errors
                this.errors = error.response?.data?.errors || {};
                this.submitted = false;

                // Display validation errors in snackbar
                if (error.response?.data?.message) {
                  this.errorMessage = error.response.data.message;
                } else {
                  this.errorMessage = 'Failed to create appointment';
                }

                // Format error details from validation errors
                this.errorDetails = [];
                if (error.response?.data?.errors) {
                  Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    if (Array.isArray(messages)) {
                      messages.forEach(msg => this.errorDetails.push(`${field}: ${msg}`));
                    } else {
                      this.errorDetails.push(`${field}: ${messages}`);
                    }
                  });
                }

                this.showErrorSnackbar = true;
              });
          })
          .catch((error) => {
            this.errors = error.response?.data?.errors || {};
            this.submitted = false;

            // Display validation errors in snackbar
            if (error.response?.data?.message) {
              this.errorMessage = error.response.data.message;
            } else {
              this.errorMessage = 'An error occurred';
            }

            // Format error details from validation errors
            this.errorDetails = [];
            if (error.response?.data?.errors) {
              Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                if (Array.isArray(messages)) {
                  messages.forEach(msg => this.errorDetails.push(`${field}: ${msg}`));
                } else {
                  this.errorDetails.push(`${field}: ${messages}`);
                }
              });
            }

            this.showErrorSnackbar = true;
          });
      }
    },
    closeForm() {
      this.$emit("closeForm");
    },
    update_stylist_list(selected_date) {
      const formattedDate = new Date(Date.parse(selected_date))
        //.toLocaleString("en-GB", { timeZone: "UTC" })
        .toLocaleString("en-GB")
        .split(",")[0];
      /*
      this.appointmentForm.datetime =
        this.appointmentForm.datetime.toISOString();
      const formattedDate = this.dateStore.selectedDate
        .toLocaleString("en-GB", { timeZone: "UTC" })
        .split(",")[0];
        */
      let stylists = [];
      if (this.apptStore.markedStylists[formattedDate] !== undefined) {
        for (let stylist in this.apptStore.markedStylists[formattedDate]) {
          let temp = this.apptStore.markedStylists[formattedDate][stylist];
          stylists.push({
            id: temp.id,
            name: temp.name,
          });
        }
      }
      this.resourcesLists = stylists;
    },
    generateEmail() {
      let emailAddress = "";
      const customer = this.appointmentForm.customer;
      if (
        typeof customer.firstname === "string" &&
        typeof customer.lastname === "string"
      ) {
        emailAddress += customer.firstname + "." + customer.lastname;
      }
      if (typeof customer.phone === "string" && customer.phone) {
        emailAddress += customer.phone.substr(-3);
      } else if (customer.phone === null || customer.phone === "") {
        const randomNumber = Math.floor(Math.random() * 999);
        emailAddress += randomNumber;
      }
      this.appointmentForm.customer.email = emailAddress + "@sshb.au";
    },
  },
};
</script>

<style>
.appointment-mode-banner {
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 0;
  font-weight: 500;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-left: 4px solid;
}

.mode-new {
  background-color: #f0f7ff;
  color: #1976d2;
  border-left-color: #1976d2;
}

.mode-update {
  background-color: #fff8f0;
  color: #f57c00;
  border-left-color: #f57c00;
}

.v-col-sm-6,
.v-col-sm-12 {
  padding: 0 6px !important;
}
.appointment-form .v-field__input {
  padding: 0px;
}
.note-block {
  padding-top: 12px;
}
.black-border .v-input__control {
  border: 1px solid lightgray;
  border-radius: 2px;
}
.appointment-form .v-select,
.appointment-form .v-autocomplete {
  margin-top: 12px;
}
.appointment-form .v-select .v-field__input,
.appointment-form .v-autocomplete .v-field__input {
  min-height: 40px;
  padding-top: 12px !important;
  padding-left: 8px !important;
  align-items: flex-start;
}
.appointment-form .v-select .v-field__field,
.appointment-form .v-autocomplete .v-field__field {
  align-items: flex-start;
  padding-top: 4px;
}
.appointment-form .black-border .v-field__field {
  padding-top: 16px;
}
.appointment-form .v-field--variant-outlined .v-label.v-field-label--floating {
  --v-field-label-scale: 0.75em;
  top: 4px !important;
  max-width: calc(100% - 8px);
}
.appointment-form .black-border .v-field--focused .v-label,
.appointment-form .black-border .v-field--active .v-label {
  opacity: 1;
}
.appointment-form input[type="text"] {
  background-color: inherit;
}

.appointment-form .v-textarea .v-field__input {
  padding: 16px 8px !important;
  min-height: 80px;
}

.appointment-form .v-textarea textarea {
  padding: 0 !important;
  font-size: 14px;
  line-height: 1.5;
}

.appointment-form .v-textarea textarea:focus,
.appointment-form .v-textarea textarea:active {
  padding: 16px 0 0 0 !important;
  font-size: 14px;
}

.appointment-form .v-textarea .v-field__overlay,
.appointment-form .v-textarea.v-field--focused .v-field__overlay,
.appointment-form .v-textarea.v-field--active .v-field__overlay {
  display: none !important;
}

.appointment-form .v-textarea.v-field--focused .v-field__input,
.appointment-form .v-textarea.v-field--active .v-field__input {
  padding: 16px 8px !important;
}

#form-buttons > .v-col {
  padding-top: 0px;
}
</style>
